/**
 * The consent state machine: one answer per fan per page, coalesced, cached and invalidated.
 *
 * **Cheapest first, and terminal for every visitor** -- the discipline `toasts/gate.js` established.
 * A visitor with no credential costs one function call and no request; a signed-in fan costs at most
 * one POST per tab per TTL; and the answer settles for somebody who never signs in rather than
 * hanging, because a caller has to be able to act on the NO.
 *
 * **`isGranted()` never rejects.** It is a `Promise<boolean>`, so it cannot express "unreadable",
 * and the failure it would otherwise throw is exactly the state where a consent gate must NOT open.
 * So an unreadable answer resolves `false`, warns once, and publishes `unavailable` to
 * `<html data-loyalty-consent>` -- the `data-loyalty-toast` precedent, so the reason is legible
 * without a debugger and so "withheld" and "we could not tell" are distinguishable in a bug report.
 * `readState()` is the richer answer for anything that needs the distinction.
 *
 * **`setGranted()` does reject**, and the difference is deliberate: a write with nobody to write for
 * is a caller error, and the join flow depends on it -- `joinLoyalty` runs inside the write's
 * `.then`, so a rejection is what stops a fan being enrolled without a consent record.
 *
 * **There is no optimistic value anywhere.** Both caches are written only from what the server
 * confirmed, so the gate can never open on a value Auth0 has not agreed to. Optimistic UI stays
 * where it already is, in `useJoinFunnel`'s local React state.
 *
 * Nothing is imported except the error classes. Every moving part is an argument, so machine.test.js
 * drives the whole thing under `node --test` with no browser.
 */

import { LoyaltyConsentAuthError, LoyaltyConsentError } from './errors.js';

/** Values published to `<html data-loyalty-consent>`. Every one of them is terminal but `reading`. */
export const STATE = {
	OFF: 'off',
	ANON: 'anon',
	READING: 'reading',
	GRANTED: 'granted',
	DENIED: 'denied',
	UNAVAILABLE: 'unavailable',
};

/**
 * Whether a published state is a DEFINITE "this fan has not consented".
 *
 * **A list of the states that ARE definite, never a list of the ones that are not**, and that is a
 * safety property rather than a style choice. `isGranted()` answers `false` for four different
 * realities -- the fan declined, the read failed, the surface is switched off, nobody is signed in
 * -- and one caller cannot treat them alike: `../toasts/gate.js` turns a definite no into a verdict
 * that `../nonMemberRedirect.js` NAVIGATES on. Enumerating the indeterminate states instead would
 * make every state added below default to "definite", so the next `unavailable`-shaped value would
 * bounce every member off their own profile until somebody remembered this line. Written this way, a
 * new STATE fails OPEN by construction, and so does a value this function has never heard of.
 *
 * `misconfigured` is deliberately absent even though it is not a STATE value at all: `./index.js`
 * rewrites `off` to it for the DOM only, and if that rewrite ever reaches a caller it has to read as
 * indeterminate, exactly as `off` does.
 *
 * @param {string} state A state from `readState()`, or anything at all.
 * @return {boolean} True only for a confirmed refusal.
 */
export function isDefiniteConsentDenial( state ) {
	return state === STATE.DENIED;
}

/**
 * Build a machine over injected everything.
 *
 * @param {Object}                                  options                     Inputs.
 * @param {Object|null}                             options.provider            `{read, write}`, or null when off.
 * @param {Object}                                  options.store               From ./store.js.
 * @param {() => string}                            options.identity            The fan's `sub`, '' when none.
 * @param {(cb: (ok: boolean) => void) => Function} options.whenAuthResolved    Settles once, yes or no.
 * @param {(cb: Function) => Function}              options.subscribeLogout     Sign-out, this tab or another.
 * @param {Object}                                  options.consentChannel      `{post, subscribe}` cross-tab.
 * @param {(cb: Function) => Function}              options.subscribeRevalidate Focus/visibility/pageshow.
 * @param {(state: string) => void}                 options.publish             Publish the state.
 * @param {() => number}                            options.now                 Epoch milliseconds.
 * @param {(message: string) => void}               options.logWarning          One-line diagnostic.
 * @return {Object} The machine.
 */
export function createConsentMachine( {
	provider,
	store,
	identity,
	whenAuthResolved,
	subscribeLogout,
	consentChannel,
	subscribeRevalidate,
	publish,
	now,
	logWarning,
} ) {
	/** The last value the server confirmed, and for whom. Null when nothing is known. */
	let known = null;

	/** The in-flight read, shared by every concurrent caller. Null when none. */
	let reading = null;

	/** The in-flight write, so two clicks serialise rather than race. Null when none. */
	let writing = Promise.resolve();

	/** Change listeners, each with its own seed. */
	const listeners = new Set();

	/** Why the last read failed, for readState(). '' when it did not. */
	let reason = '';

	/**
	 * The last state announced, for readState(). '' before anything has been asked.
	 *
	 * **This is the machine's own answer to "why did you say no", and it is a stronger seam than
	 * `reason`.** `Promise<boolean>` cannot express "unreadable", and a caller that has to tell a fan
	 * who WITHHELD consent from a read that FAILED has no other way to do it. `toasts/gate.js` is
	 * that caller: it settles one verdict for a definite `denied` and a different, fail-open one for
	 * everything else, because a redirect must not move a member during a Management API outage.
	 *
	 * **Why not `reason`.** It is only ever set on a FAILURE, so it is `''` for a granted answer, a
	 * denied one, an anonymous browser and a switched-off surface alike -- it can say that something
	 * broke, never that a fan said no. Identifying a denial from it means inferring one from
	 * `checkedAt > 0 && reason === ''`, i.e. from this file's internal bookkeeping. `state` says it.
	 *
	 * **A terminal state is announced SYNCHRONOUSLY before `isGranted()` resolves** on the paths that
	 * settle an answer for the current fan -- the `off` branch, `forget( STATE.ANON )`, the
	 * cached-store branch, `remember()` on readRemote's success leg, and `STATE.UNAVAILABLE` on its
	 * failure leg. That is what makes reading this immediately after the promise settles sound.
	 *
	 * **It is the machine's current position, not a settled verdict, and two paths prove it**, which
	 * is why `isDefiniteConsentDenial()` above is a whitelist rather than a check for the states it
	 * is not:
	 *
	 * 1. A read that succeeds *after the fan changed mid-flight* answers out of `known` without
	 *    calling `remember()`, so nothing terminal is announced for it: this reads `reading`, or
	 *    `anon` where the identity change came through `subscribeLogout`.
	 * 2. `subscribeRevalidate` re-runs `isGranted()` on focus once the tab cache has lapsed, which
	 *    announces `reading` again *after* a terminal answer.
	 *
	 * Neither is `denied`, so a caller keyed on a denial stays open through both -- the right way to
	 * be wrong about an answer that is in flight or whose owner has left.
	 */
	let lastState = '';

	/**
	 * @param {string} state A STATE value.
	 * @return {void}
	 */
	function announceState( state ) {
		// Recorded before publishing, and outside the try: a `publish` that throws is a broken
		// diagnostic channel, not a reason for the machine to forget what it decided.
		lastState = state;

		try {
			publish( state );
		} catch ( error ) {
			// Publishing is diagnostics. It must never be the reason a read fails.
		}
	}

	/**
	 * Tell every listener, once, and only about a real transition.
	 *
	 * The set is copied before iterating, so a listener that unsubscribes or throws mid-iteration
	 * cannot strand the ones behind it. `resources/scripts/auth/store.js`'s `announce()` applies the
	 * same discipline; unlike it, the set is not cleared, because these are standing subscriptions
	 * rather than one-shots.
	 *
	 * @param {boolean} granted The new value.
	 * @return {void}
	 */
	function notify( granted ) {
		[ ...listeners ].forEach( entry => {
			if ( entry.last === granted ) {
				return;
			}

			entry.last = granted;

			try {
				entry.listener( granted );
			} catch ( error ) {
				// One listener's failure is not the others' problem, and is certainly not the
				// caller-of-setGranted's problem.
			}
		} );
	}

	/**
	 * Record a confirmed value, in every tier, and tell everyone if it moved.
	 *
	 * @param {string}  sub     Whose value this is.
	 * @param {boolean} granted The confirmed value.
	 * @param {number}  at      When it was confirmed, epoch ms.
	 * @return {void}
	 */
	function remember( sub, granted, at ) {
		const changed = ! known || known.sub !== sub || known.granted !== granted;

		known = { sub, granted, at };
		reason = '';
		store.write( sub, granted );
		announceState( granted ? STATE.GRANTED : STATE.DENIED );

		if ( changed ) {
			notify( granted );
		}
	}

	/**
	 * Drop everything remembered. Called on a sign-out and on an identity change.
	 *
	 * @param {string} state What to publish afterwards.
	 * @return {void}
	 */
	function forget( state ) {
		const had = known;

		known = null;
		reading = null;
		reason = '';
		store.forget();
		announceState( state );

		// A fan who signed out is not a fan who withdrew consent, but every consumer of this signal
		// is a gate, and both mean "closed" to a gate. Announcing keeps an open tab from acting on a
		// value whose owner has left.
		if ( had && had.granted ) {
			notify( false );
		}
	}

	/**
	 * Wait until it is known whether there is a fan at all, then answer.
	 *
	 * @return {Promise<string>} The fan's `sub`, or '' when there is none.
	 */
	function resolveIdentity() {
		const sub = identity();

		if ( sub ) {
			return Promise.resolve( sub );
		}

		return new Promise( resolve => {
			let settled = false;
			let stop = null;

			// `whenAuthResolved` may fire SYNCHRONOUSLY when the fan is already known, which can
			// settle everything before it has returned a handle to tear down -- so the handle
			// cannot be referenced from inside the callback. Getting it wrong here threw a
			// ReferenceError out of the promise executor and turned every anonymous read into a
			// rejection. ../urcAuth.js's `whenSignedIn()` carries the same guard for the same
			// reason.
			const done = () => {
				if ( settled ) {
					return;
				}

				settled = true;

				if ( stop ) {
					stop();
				}

				resolve( identity() );
			};

			stop = whenAuthResolved( done );

			if ( settled ) {
				stop();
			}
		} );
	}

	/**
	 * One read, shared.
	 *
	 * @param {string} sub The fan.
	 * @return {Promise<boolean>} The value, or false when it could not be read.
	 */
	function readRemote( sub ) {
		if ( reading && reading.sub === sub ) {
			return reading.promise;
		}

		announceState( STATE.READING );

		const promise = provider.read().then(
			answer => {
				// The fan may have changed while this was in flight -- a sign-out, or a sign-in as
				// somebody else. Attaching this answer to the current identity would be attaching
				// it to the wrong fan.
				reading = null;

				if ( identity() !== sub ) {
					return Boolean( known && known.sub === identity() && known.granted );
				}

				remember( sub, answer.granted, answer.checkedAt ? answer.checkedAt * 1000 : now() );

				return answer.granted;
			},
			error => {
				reading = null;

				// Fails CLOSED, and this is the load-bearing line of the whole module. Guessing
				// `true` here means sending a fan's identity to a processor when the record that
				// says we may is the thing that failed to load. `toasts/gate.js` fails closed for a
				// smaller reason than this one.
				reason = error instanceof LoyaltyConsentAuthError ? 'no-credential' : 'unreadable';
				announceState( STATE.UNAVAILABLE );
				logWarning(
					`loyalty consent could not be read (${ reason }); treating as not granted`
				);

				return false;
			}
		);

		reading = { sub, promise };

		return promise;
	}

	/**
	 * Whether the current fan has granted loyalty consent. Never rejects.
	 *
	 * @return {Promise<boolean>} The answer.
	 */
	function isGranted() {
		if ( ! provider ) {
			announceState( STATE.OFF );

			return Promise.resolve( false );
		}

		return resolveIdentity().then( sub => {
			if ( ! sub ) {
				// Not an error, and specifically not a rejection: "nobody is signed in" is one of
				// the states the contract requires this to answer `false` for.
				forget( STATE.ANON );

				return false;
			}

			/*
			 * The store is the only thing that says whether a known value is still usable, and it
			 * is consulted even when `known` already holds one for this fan.
			 *
			 * `known` used to be trusted on its own, which pinned the answer for the LIFE OF THE
			 * PAGE: a tab left open and focused never re-read, so the documented staleness bound of
			 * `client_ttl + server_ttl` was really "until this tab is closed". On a consent flag
			 * that is the wrong direction to be wrong in. `store.read()` applies the TTL to its own
			 * in-memory memo as well as to sessionStorage, so this is correct in a private window
			 * too.
			 */
			const cached = store.read( sub );

			if ( cached ) {
				known = { sub, granted: cached.granted, at: cached.checkedAt };
				announceState( cached.granted ? STATE.GRANTED : STATE.DENIED );

				return cached.granted;
			}

			// Nothing usable. Drop a lapsed value rather than letting it answer the next caller.
			if ( known && known.sub === sub ) {
				known = null;
			}

			return readRemote( sub );
		} );
	}

	/**
	 * Persist the fan's consent.
	 *
	 * Serialised behind any write already in flight, so two clicks produce two ordered writes rather
	 * than a race whose winner is arbitrary. Across tabs Auth0 is last-write-wins and cannot be made
	 * otherwise; what is fixed instead is the visible symptom, by broadcasting the server's answer so
	 * both tabs converge on what is actually stored.
	 *
	 * @param {boolean} granted         Whether consent is being given.
	 * @param {Object}  [options]       Write options.
	 * @param {boolean} [options.force] Write even when the cache already holds this value.
	 * @return {Promise<void>} Resolves once Auth0 has confirmed.
	 */
	function setGranted( granted, options ) {
		const wanted = Boolean( granted );
		const force = Boolean( options && options.force );

		const run = () => {
			if ( ! provider ) {
				return Promise.reject(
					new LoyaltyConsentError(
						'Loyalty consent is not configured; nothing was persisted.'
					)
				);
			}

			const sub = identity();

			if ( ! sub ) {
				// Before the wire, and before any TTL check: there is nobody to record consent for.
				return Promise.reject( new LoyaltyConsentAuthError() );
			}

			// Already confirmed at this value for this fan. Double-clicks are free, and the join
			// flow's write costs nothing for a fan who has already consented -- correctly, because
			// the record already says so.
			//
			// **`force` exists because "the record already says so" stopped being the whole story
			// on 2026-09-22.** The write now carries a second key, the withdrawal date, and a fan
			// whose cached answer is already `false` -- another tab or another device withdrew while
			// this one had the dialog open -- would take this exit and produce NOTHING: no PATCH, no
			// date, and no audit line saying they asked. That is silent on every surface, and it is
			// silent in the one case where the record of the asking is the point. A withdrawal
			// therefore always reaches Auth0; a repeat simply re-stamps the date, which is a later
			// and equally true answer to "when did this site last hear from them".
			if (
				! force &&
				known &&
				known.sub === sub &&
				known.granted === wanted &&
				store.read( sub )
			) {
				return Promise.resolve();
			}

			return provider.write( wanted ).then(
				answer => {
					remember(
						sub,
						answer.granted,
						answer.checkedAt ? answer.checkedAt * 1000 : now()
					);
					consentChannel.post( { sub, granted: answer.granted, at: now() } );
				},
				error => {
					// The stored value is now unknown rather than unchanged, so the caches go. A
					// rejected write must leave no optimistic residue at all.
					known = null;
					store.forget();

					throw error;
				}
			);
		};

		writing = writing.then( run, run );

		return writing;
	}

	/**
	 * Subscribe to changes.
	 *
	 * Change-only, and seeded per listener at subscription time -- `tracking/consent.js`'s discipline,
	 * for the reason it gives there: the first notification should be a change from what the caller
	 * has already acted on, not from an assumed default.
	 *
	 * @param {(granted: boolean) => void} listener Called on each transition.
	 * @return {() => void} Unsubscribe. Safe to call more than once.
	 */
	function onChange( listener ) {
		const entry = { listener, last: known ? known.granted : null };

		listeners.add( entry );

		return () => {
			listeners.delete( entry );
		};
	}

	/**
	 * The full state, for diagnostics and for anything that must tell `false` from "unknown".
	 *
	 * **`state` is what the machine last ANNOUNCED, and it is the only field that separates a refusal
	 * from a failure.** `granted` is a boolean, and `reason` is set only by a failed read -- so
	 * neither can tell `denied` from `off` or `anon`. `../toasts/gate.js` has to, because one of the
	 * three subscribers riding its verdict navigates. Read it through
	 * `isDefiniteConsentDenial()`, never by comparing the string, and see `lastState` above for the
	 * two paths on which it is not a settled verdict.
	 *
	 * @return {{granted: boolean, checkedAt: number, stale: boolean, reason: string, state: string}} The state.
	 */
	function readState() {
		return {
			granted: Boolean( known && known.granted ),
			checkedAt: known ? known.at : 0,
			stale: Boolean( known && store.read( known.sub ) === null ),
			reason,
			state: lastState,
		};
	}

	// A sign-out, here or in another tab. session.js already honours the `auth0_logged_out`
	// tombstone, so `identity()` stops naming a fan on its own -- this is what makes an OPEN tab act
	// on it rather than waiting for its next read.
	subscribeLogout( () => forget( STATE.ANON ) );

	// Our own writes, from another tab. A fan withdrawing consent in one tab while another keeps
	// issuing loyalty requests is precisely the failure this feature exists to prevent, and nothing
	// else would tell that tab.
	consentChannel.subscribe( message => {
		if ( ! message || typeof message.granted !== 'boolean' ) {
			return;
		}

		const sub = identity();

		// Only for this tab's own fan. A message about somebody else is a different browser profile's
		// business, and adopting it is how one fan's value would reach another.
		if ( ! sub || message.sub !== sub ) {
			return;
		}

		remember( sub, message.granted, Number( message.at ) || now() );
	} );

	// A change made on another device shows up here, and nowhere else: these three events are what
	// `resources/scripts/auth/index.js` also watches to re-resolve the session, `pageshow`
	// included because some engines replay neither of the others after a bfcache restore.
	subscribeRevalidate( () => {
		const sub = identity();

		if ( ! sub || ! provider ) {
			return;
		}

		// Only when the tab cache has lapsed. Re-reading on every focus would turn a fan alt-tabbing
		// into a per-focus Management API read.
		if ( store.read( sub ) === null ) {
			known = null;
			reading = null;
			isGranted();
		}
	} );

	/**
	 * Adopt a value the SERVER confirmed through a route other than this machine's own write: a
	 * membership begin or advance (../membership/). Same tiers, same broadcast as a confirmed
	 * `setGranted()`, so every tab's gate flips together -- and same rule, that only a server answer
	 * is ever remembered, never what was asked for.
	 *
	 * @param {{granted: boolean, checkedAt: number}} answer The server's answer.
	 * @return {void}
	 */
	function applyServerAnswer( answer ) {
		const sub = identity();

		if ( ! sub || ! answer || typeof answer.granted !== 'boolean' ) {
			return;
		}

		remember( sub, answer.granted, answer.checkedAt ? answer.checkedAt * 1000 : now() );
		consentChannel.post( { sub, granted: answer.granted, at: now() } );
	}

	return { isGranted, setGranted, onChange, readState, applyServerAnswer };
}
